import React, { useState, useEffect, useMemo } from 'react';

const paymentMethods = [
  { value: 'efectivo', label: '💵 Efectivo', requiresRef: false },
  { value: 'tarjeta', label: '💳 Tarjeta', requiresRef: false },
  { value: 'transferencia', label: '🏦 Transferencia', requiresRef: true },
  { value: 'otros', label: '🏷️ Otros', requiresRef: false },
  { value: 'cuentaCorriente', label: '📋 Cuenta Corriente', requiresRef: false }
];

const MultiplePaymentSelector = ({
  total,
  onPaymentsChange,
  clientes,
  clienteSeleccionado,
  setClienteSeleccionado
}) => {
  // Un estado para cada método de pago
  const [inputs, setInputs] = useState(() => {
    const initial = paymentMethods.reduce((acc, m) => {
      acc[m.value] = { monto: '', referencia: '', observaciones: '' };
      return acc;
    }, {});
    // Por defecto, efectivo = total
    initial['efectivo'].monto = total ? total.toFixed(2) : '';
    return initial;
  });

  // Resetear todos los campos cuando cambia el total (venta realizada o nueva venta)
  useEffect(() => {
    const reset = paymentMethods.reduce((acc, m) => {
      acc[m.value] = { monto: '', referencia: '', observaciones: '' };
      return acc;
    }, {});
    reset['efectivo'].monto = total ? total.toFixed(2) : '';
    setInputs(reset);
    setClienteSeleccionado && setClienteSeleccionado(null);
  }, [total]);

  // Calcular totales y validaciones
  const { totalPagos, diferencia, isValid, validationMessage } = useMemo(() => {
    let totalPagos = 0;
    paymentMethods.forEach(m => {
      const monto = parseFloat(inputs[m.value].monto) || 0;
      totalPagos += monto;
    });
    const diferencia = total - totalPagos;
    const isValid = Math.abs(diferencia) < 0.01;
    let validationMessage = '';
    if (diferencia > 0.01) {
      validationMessage = `Faltan $${diferencia.toFixed(2)}`;
    } else if (diferencia < -0.01) {
      validationMessage = `Excede por $${Math.abs(diferencia).toFixed(2)}`;
    }
    return { totalPagos, diferencia, isValid, validationMessage };
  }, [inputs, total]);

  // Callback al padre
  useEffect(() => {
    const validPayments = paymentMethods
      .map(m => ({
        forma_pago: m.value,
        ...inputs[m.value],
      }))
      .filter(p => parseFloat(p.monto) > 0);
    const hasRequiredReferences = paymentMethods.every(m => {
      if (!parseFloat(inputs[m.value].monto)) return true;
      return !m.requiresRef || (inputs[m.value].referencia && inputs[m.value].referencia.trim());
    });
    const finalIsValid = isValid && hasRequiredReferences &&
      (!parseFloat(inputs['cuentaCorriente'].monto) || clienteSeleccionado);
    onPaymentsChange(validPayments, finalIsValid);
  }, [inputs, isValid, clienteSeleccionado, onPaymentsChange]);

  // Handlers
  const handleInput = (method, value) => {
    setInputs(prev => ({
      ...prev,
      [method]: {
        ...prev[method],
        monto: value
      }
    }));
    if (method === 'cuentaCorriente' && (!value || parseFloat(value) === 0)) {
      setClienteSeleccionado && setClienteSeleccionado(null);
    }
  };

  // Botón para borrar el input
  const clearInput = (method) => {
    setInputs(prev => ({
      ...prev,
      [method]: {
        ...prev[method],
        monto: ''
      }
    }));
    if (method === 'cuentaCorriente') {
      setClienteSeleccionado && setClienteSeleccionado(null);
    }
  };

  // Botón para poner el total en el input y borrar el resto
  const setTotalInput = (method) => {
    setInputs(prev => {
      const updated = {};
      paymentMethods.forEach(m => {
        updated[m.value] = {
          ...prev[m.value],
          monto: m.value === method ? (total ? total.toFixed(2) : '') : ''
        };
      });
      return updated;
    });
    if (method === 'cuentaCorriente') {
      // Si es cuenta corriente, limpiar el cliente si se borra
      setClienteSeleccionado && setClienteSeleccionado(null);
    }
  };

  // Sugerir monto restante para un método
  const suggestRestante = (method) => {
    let totalIngresado = 0;
    paymentMethods.forEach(m => {
      if (m.value !== method) {
        totalIngresado += parseFloat(inputs[m.value].monto) || 0;
      }
    });
    const sugerido = Math.max(0, total - totalIngresado).toFixed(2);
    handleInput(method, sugerido);
  };

  // Determinar si mostrar el selector de cliente
  const showClienteSelector = parseFloat(inputs['cuentaCorriente'].monto) > 0;

  // Armar los elementos del grid: 5 métodos + 1 selector (si aplica)
  const gridItems = [
    ...paymentMethods.map((method) => (
      <div key={method.value} className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm flex flex-col gap-1 min-h-[90px]">
        <div className="flex items-center mb-1">
          <span className="font-semibold text-gray-700 text-xs md:text-base">{method.label}</span>
        </div>
        <div className="flex items-center gap-1">
          <input
            type="number"
            step="0.01"
            min="0"
            value={inputs[method.value].monto}
            onChange={e => handleInput(method.value, e.target.value)}
            className="w-20 md:w-24 p-1 md:p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
            placeholder="0.00"
          />
          <button
            type="button"
            className="text-xs text-blue-600 hover:text-blue-800"
            tabIndex={-1}
            onClick={() => suggestRestante(method.value)}
            title="Autocompletar con el restante"
          >
            Sugerir
          </button>
          <button
            type="button"
            className="text-xs text-green-600 hover:text-green-800 ml-1"
            tabIndex={-1}
            onClick={() => setTotalInput(method.value)}
            title="Poner el total en este método"
          >
            Total
          </button>
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-red-600 ml-1"
            tabIndex={-1}
            onClick={() => clearInput(method.value)}
            title="Borrar importe"
          >
            Borrar
          </button>
        </div>
        {method.requiresRef && (
          <div className="mt-1">
            <input
              type="text"
              value={inputs[method.value].referencia}
              onChange={e => setInputs(prev => ({
                ...prev,
                [method.value]: {
                  ...prev[method.value],
                  referencia: e.target.value
                }
              }))}
              className={`w-full p-1 md:p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm ${parseFloat(inputs[method.value].monto) > 0 && !inputs[method.value].referencia?.trim()
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300'
                }`}
              placeholder="Referencia (obligatorio)"
            />
            {parseFloat(inputs[method.value].monto) > 0 && !inputs[method.value].referencia?.trim() && (
              <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
            )}
          </div>
        )}
      </div>
    )),
    showClienteSelector && (
      <div
        key="cuentaCorrienteCliente"
        className="bg-white border-2 border-amber-400 rounded-xl shadow p-4 flex flex-col items-center min-h-[90px]"
        style={{
          position: 'sticky',
          left: 0,
          bottom: 0,
          zIndex: 20,
          marginTop: 8,
        }}
      >
        <label className="text-base font-bold text-amber-700 mb-2 flex items-center gap-2">
          <span className="text-2xl">⚠️</span> Cliente para Cuenta Corriente
        </label>
        <select
          value={clienteSeleccionado || ''}
          onChange={e => setClienteSeleccionado(e.target.value || null)}
          className="w-full max-w-xs p-3 border-2 border-amber-400 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-base font-semibold text-amber-800"
        >
          <option value="">Seleccionar cliente</option>
          {clientes.map((cliente) => (
            <option key={cliente.id} value={cliente.id}>
              {cliente.nombre} - {cliente.telefono}
            </option>
          ))}
        </select>
        {!clienteSeleccionado && (
          <p className="text-sm text-amber-700 mt-2 font-semibold">
            Debe seleccionar un cliente para pagos con cuenta corriente
          </p>
        )}
      </div>
    )
  ].filter(Boolean);

  return (
    <div className="bg-white rounded-xl p-2 md:p-4 border border-blue-100 shadow w-full max-w-full overflow-x-auto mb-32">
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3 w-full"
        style={{
          maxHeight: 'calc(100vh - 300px)', // deja espacio para sticky abajo y header arriba
          overflowY: 'auto',
          paddingBottom: 8
        }}
      >
        {gridItems}
      </div>
    </div>
  );
};

export default MultiplePaymentSelector;
